/* global HTMLDivElement, KeyboardEvent */
// Morning Planning Modal (D20) — select 1–3 MIT tasks for the day, reorder with up/down, save DailyPlan.
// D22: shows capacity bar (planned Σ estimateMinutes vs day capacity) + overcommit warning.
// No-orphan upgrade: search input below suggestions filters ALL todo/doing tasks (max 8 results).
import { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Sun, X } from "lucide-react";
import { todayIso } from "../lib/date";
import { getDayCapacityMinutes } from "../lib/app-prefs";
import type { LifeOsApi } from "../lib/life-os-contract";
import type { Task } from "../types";
import { isOverCapacity, plannedMinutes, suggestMits } from "./today-rituals-derive";
import { autoScheduleDay } from "./auto-schedule-derive";
import { timeToMinutes } from "./calendar-week-grid-derive";
import { expandRecurrence } from "../lib/recurrence";
import { PRIO, PRIO_LABEL } from "./bento-ui";
import { useFocusTrap } from "../lib/focus-trap";
import { InfoHint } from "./info-hint";
import { aiAvailable } from "../lib/ai/ai-config";
import { AiPlanSuggestButton } from "./ai-plan-suggest-button";
import type { PlanPick } from "../lib/ai/plan-suggest";

interface Props {
  api: LifeOsApi;
  onClose: () => void;
}

const MAX_MIT = 3;

export function MorningPlanningModal({ api, onClose }: Props) {
  const today = todayIso();
  const candidates = suggestMits(api.state, today);
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef, true);

  // Escape closes the modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Search query for "all tasks" section
  const [search, setSearch] = useState("");

  // Selected MIT ids in chosen order
  const [selected, setSelected] = useState<string[]>(() => {
    const existing = api.state.dailyPlans.find((p) => p.id === today);
    return existing ? existing.mitTaskIds : [];
  });
  // D54: lý do AI gợi ý cho mỗi việc (hiển thị dưới tiêu đề trong danh sách thứ tự).
  const [aiReasons, setAiReasons] = useState<Record<string, string>>({});

  // AI picks → set order + reasons. User vẫn sửa/sắp lại/lưu như thường.
  function handleAiPicks(picks: PlanPick[]) {
    if (picks.length === 0) return;
    setSelected(picks.map((p) => p.taskId).slice(0, MAX_MIT));
    setAiReasons(Object.fromEntries(picks.map((p) => [p.taskId, p.reason])));
  }

  function toggle(taskId: string) {
    setSelected((prev) => {
      if (prev.includes(taskId)) return prev.filter((id) => id !== taskId);
      if (prev.length >= MAX_MIT) return prev; // cap at 3
      return [...prev, taskId];
    });
  }

  function moveUp(idx: number) {
    if (idx === 0) return;
    setSelected((prev) => {
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next;
    });
  }

  function moveDown(idx: number) {
    setSelected((prev) => {
      if (idx >= prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next;
    });
  }

  function save() {
    if (selected.length === 0) return;
    const existing = api.state.dailyPlans.find((p) => p.id === today);
    api.actions.saveDailyPlan({
      id: today,
      date: today,
      mitTaskIds: selected,
      createdAt: existing?.createdAt ?? new Date().toISOString(),
      locked: true, // D23: lock the day once planned; Today offers emergency re-plan
      ...(existing?.replanReason ? { replanReason: existing.replanReason } : {}),
    });
    onClose();
  }

  // Build a quick lookup for titles + estimates
  const taskById = new Map<string, Task>(api.state.tasks.map((t) => [t.id, t]));

  // P4-3: deterministic auto time-blocking — place selected MITs into today's
  // free gaps (09:00–18:00), skipping existing events, then save + close.
  function autoSchedule() {
    if (selected.length === 0) return;
    const todays = api.state.events.filter((e) => expandRecurrence(e, today, today).length > 0);
    const blockedTaskIds = new Set(todays.map((e) => e.linkedTaskId).filter(Boolean) as string[]);
    const busy = todays.map((e) => ({
      start: e.time,
      durationMin: e.durationMin ?? (e.endTime ? timeToMinutes(e.endTime) - timeToMinutes(e.time) : 60),
    }));
    const mits = selected
      .map((id) => taskById.get(id))
      .filter((t): t is Task => Boolean(t) && !blockedTaskIds.has(t!.id))
      .map((t) => ({ id: t.id, title: t.title, estimateMinutes: t.estimateMinutes }));
    for (const b of autoScheduleDay({ mits, busy })) {
      api.actions.addEvent({ title: b.title, date: today, time: b.time, durationMin: b.durationMin, linkedTaskId: b.taskId });
    }
    save(); // persist the plan (locks the day) + close
  }

  // All actionable (todo/doing) tasks available for search — excludes candidate ids to avoid duplication
  const candidateIds = new Set(candidates.map((t) => t.id));
  const searchQuery = search.trim().toLowerCase();
  const searchResults: Task[] = searchQuery
    ? api.state.tasks
        .filter(
          (t) =>
            (t.status === "todo" || t.status === "doing") &&
            !candidateIds.has(t.id) &&
            t.title.toLowerCase().includes(searchQuery),
        )
        .slice(0, 8)
    : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="morning-planning-title">
      <div ref={panelRef} className="relative w-full max-w-md rounded-3xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="mb-4 flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-2xl bg-brand-100 text-brand-600">
            <Sun className="h-5 w-5" />
          </span>
          <div>
            <h2 id="morning-planning-title" className="flex items-center gap-1.5 font-display text-[18px] font-bold">
              Lập kế hoạch sáng
              <InfoHint label="MIT là gì?" text="MIT = Most Important Tasks: 1–3 việc quan trọng nhất, làm xong là ngày coi như thành công. Tập trung thay vì danh sách dài." />
            </h2>
            <p className="text-[12px] text-ink-500">Chọn 1–3 việc quan trọng nhất hôm nay</p>
          </div>
          <button className="ml-auto rounded-xl p-1.5 hover:bg-ink-100" onClick={onClose} aria-label="Đóng">
            <X className="h-4 w-4 text-ink-500" />
          </button>
        </div>

        {/* D54: AI gợi ý MIT — đặt sẵn 1–3 việc + lý do; user vẫn sửa/sắp lại/lưu */}
        {aiAvailable(api) && (
          <div className="mb-4">
            <div className="mb-1.5 flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest text-ink-500">
              Trợ lý lập kế hoạch
              <InfoHint
                label="AI chọn MIT thế nào?"
                text="AI ưu tiên: việc QUÁ HẠN → ưu tiên CAO → gắn MỤC TIÊU đang theo, trong giới hạn SỨC CHỨA ngày. Thêm tiêu chí riêng của bạn ở Cài đặt → Trợ lý AI."
              />
            </div>
            <AiPlanSuggestButton api={api} capacityMin={getDayCapacityMinutes()} onResult={handleAiPicks} />
          </div>
        )}

        {/* Selected MIT order — shown only when items selected */}
        {selected.length > 0 && (
          <div className="mb-4">
            <p className="mb-2 text-[11px] font-bold uppercase tracking-widest text-ink-500">Thứ tự ưu tiên</p>
            <div className="space-y-1.5">
              {selected.map((id, idx) => {
                const task = taskById.get(id);
                if (!task) return null;
                return (
                  <div key={id} className="flex items-start gap-2 rounded-xl bg-brand-50 px-3 py-2">
                    <span className="gtext font-display text-[15px] font-bold w-4 shrink-0">{idx + 1}</span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[13px] font-semibold">{task.title}</p>
                      {aiReasons[id] && (
                        <p className="mt-0.5 text-[11px] leading-snug text-brand-600/80">✨ {aiReasons[id]}</p>
                      )}
                    </div>
                    <div className="flex shrink-0 gap-0.5">
                      <button onClick={() => moveUp(idx)} disabled={idx === 0} className="grid h-6 w-6 place-items-center rounded-lg text-ink-400 hover:bg-white disabled:opacity-30" aria-label="Lên">
                        <ChevronUp className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => moveDown(idx)} disabled={idx === selected.length - 1} className="grid h-6 w-6 place-items-center rounded-lg text-ink-400 hover:bg-white disabled:opacity-30" aria-label="Xuống">
                        <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => toggle(id)} className="grid h-6 w-6 place-items-center rounded-lg text-ink-400 hover:bg-white hover:text-rose-500" aria-label="Bỏ chọn">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Suggestions section */}
        <p className="mb-2 text-[11px] font-bold uppercase tracking-widest text-ink-500">Gợi ý</p>
        {candidates.length === 0 ? (
          <p className="py-4 text-center text-[13px] text-ink-400">Không có việc nào cần làm hôm nay 🎉</p>
        ) : (
          <div className="max-h-48 overflow-y-auto space-y-1.5 pr-1">
            {candidates.map((task) => <TaskRow key={task.id} task={task} selected={selected} onToggle={toggle} />)}
          </div>
        )}

        {/* Search — all todo/doing tasks */}
        <div className="mt-3">
          <div className="mb-2 flex items-center gap-2">
            <div className="h-px flex-1 bg-ink-200/60" />
            <p className="text-[11px] font-bold uppercase tracking-widest text-ink-400">Tất cả việc</p>
            <div className="h-px flex-1 bg-ink-200/60" />
          </div>
          <input
            type="text"
            placeholder="Tìm việc khác…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-xl border border-ink-200 bg-white px-3 py-2 text-[13px] outline-none focus:border-brand-400 placeholder:text-ink-400"
          />
          {searchQuery && (
            <div className="mt-1.5 max-h-40 overflow-y-auto space-y-1.5 pr-1">
              {searchResults.length === 0 ? (
                <p className="py-3 text-center text-[12px] text-ink-400">Không tìm thấy kết quả</p>
              ) : (
                searchResults.map((task) => <TaskRow key={task.id} task={task} selected={selected} onToggle={toggle} />)
              )}
            </div>
          )}
        </div>

        {/* Capacity bar (D22) */}
        <CapacityBar selected={selected} taskById={taskById} />

        {/* P4-3: one-click auto time-blocking into today's free gaps */}
        {selected.length > 0 && (
          <button
            onClick={autoSchedule}
            className="mt-3 w-full rounded-xl border border-brand-200 bg-brand-50 py-2.5 text-[13px] font-bold text-brand-600 transition hover:bg-brand-100"
          >
            🗓️ Tự xếp {selected.length} việc vào khung giờ trống (09:00–18:00)
          </button>
        )}

        {/* Footer */}
        <div className="mt-4 flex items-center justify-between gap-3">
          <p className="text-[12px] text-ink-400">{selected.length}/{MAX_MIT} đã chọn</p>
          <div className="flex gap-2">
            <button onClick={onClose} className="rounded-xl border border-ink-200 px-4 py-2 text-[13px] font-semibold hover:bg-ink-50">
              Huỷ
            </button>
            <button
              onClick={save}
              disabled={selected.length === 0}
              className="gbrand rounded-xl px-4 py-2 text-[13px] font-bold text-white disabled:opacity-40"
            >
              Lưu kế hoạch
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TaskRow — shared row for both suggestions and search results lists.
// ---------------------------------------------------------------------------
interface TaskRowProps {
  task: Task;
  selected: string[];
  onToggle: (id: string) => void;
}

function TaskRow({ task, selected, onToggle }: TaskRowProps) {
  const isSelected = selected.includes(task.id);
  const disabled = !isSelected && selected.length >= MAX_MIT;
  return (
    <button
      onClick={() => !disabled && onToggle(task.id)}
      className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition ${
        isSelected ? "bg-brand-100 ring-1 ring-brand-400" : "bg-ink-100/40 hover:bg-ink-100"
      } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
    >
      <span
        className={`grid h-5 w-5 shrink-0 place-items-center rounded-full border-2 text-[10px] font-bold transition ${
          isSelected ? "border-brand-500 bg-brand-500 text-white" : "border-ink-300"
        }`}
      >
        {isSelected ? selected.indexOf(task.id) + 1 : ""}
      </span>
      <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: PRIO[task.priority] }} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-semibold">{task.title}</p>
        <p className="text-[11px] text-ink-500">{task.dueDate} · {task.estimateMinutes}m · {PRIO_LABEL[task.priority]}</p>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// CapacityBar — shows Σ estimateMinutes vs day capacity, warns on overcommit.
// ---------------------------------------------------------------------------
interface CapacityBarProps {
  selected: string[];
  taskById: Map<string, Task>;
}

function CapacityBar({ selected, taskById }: CapacityBarProps) {
  const capacityMin = getDayCapacityMinutes();
  const tasks = [...taskById.values()];
  const planned = plannedMinutes(selected, tasks.filter((t) => selected.includes(t.id)));
  const over = isOverCapacity(planned, capacityMin);
  const pct = Math.min(100, Math.round((planned / capacityMin) * 100));

  const barColor = over ? "bg-rose-500" : pct >= 80 ? "bg-amber-400" : "bg-brand-500";
  const labelColor = over ? "text-rose-600" : pct >= 80 ? "text-amber-600" : "text-ink-500";

  return (
    <div className="mt-4">
      <div className="mb-1 flex items-center justify-between">
        <p className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest text-ink-400">
          Sức chứa ngày
          <InfoHint label="Sức chứa ngày là gì?" text="Tổng thời gian ước tính bạn thực sự làm việc được trong ngày. Tổng thời lượng các MIT vượt mức này = bạn đang ôm quá nhiều việc." />
        </p>
        <p className={`text-[11px] font-semibold ${labelColor}`}>
          {planned}m / {capacityMin}m
        </p>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-ink-200/50">
        <div
          className={`h-full rounded-full transition-all duration-300 ${barColor}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {over && (
        <p className="mt-1.5 text-[11px] font-semibold text-rose-500">
          Vượt sức chứa {planned - capacityMin}m — cân nhắc giảm bớt việc.
        </p>
      )}
    </div>
  );
}
